<script setup>
import { ref, watch } from 'vue';
import agentsSidecarAPI from 'dashboard/api/agentsSidecar';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const isLoading = ref(false);
const isSaving = ref(false);
const isResetting = ref(false);
const error = ref(null);
const saveMessage = ref(null);
const config = ref(null);
const expandedPrompts = ref({});

const LLM_PROVIDERS = ['google', 'openai', 'anthropic'];

const mergeConfigWithOverrides = rawConfig => {
  // Start with defaults from agent-graphos
  const merged = {
    llm_provider: rawConfig.llm_provider,
    llm_model: rawConfig.llm_model,
    agents: rawConfig.agents.map(agent => ({
      ...agent,
      // Initialize system_prompt from prompt_preview if not set
      system_prompt: agent.system_prompt || agent.prompt_preview || '',
    })),
  };

  // Apply account overrides if they exist
  const overrides = rawConfig.account_overrides || {};

  if (overrides.llm_provider) {
    merged.llm_provider = overrides.llm_provider;
  }
  if (overrides.llm_model) {
    merged.llm_model = overrides.llm_model;
  }

  // Merge agent-specific overrides
  if (overrides.agents && Array.isArray(overrides.agents)) {
    overrides.agents.forEach(override => {
      const agentIndex = merged.agents.findIndex(a => a.name === override.name);
      if (agentIndex !== -1) {
        if (override.temperature !== undefined) {
          merged.agents[agentIndex].temperature = override.temperature;
        }
        if (override.max_tokens !== undefined) {
          merged.agents[agentIndex].max_tokens = override.max_tokens;
        }
        if (override.system_prompt !== undefined) {
          merged.agents[agentIndex].system_prompt = override.system_prompt;
        }
      }
    });
  }

  return merged;
};

const fetchConfig = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const rawConfig = await agentsSidecarAPI.getConfig();
    config.value = mergeConfigWithOverrides(rawConfig);
  } catch (err) {
    error.value = err.message;
  } finally {
    isLoading.value = false;
  }
};

const saveConfig = async () => {
  isSaving.value = true;
  saveMessage.value = null;

  try {
    const payload = {
      llm_provider: config.value.llm_provider,
      llm_model: config.value.llm_model,
      agents: config.value.agents.map(agent => ({
        name: agent.name,
        temperature: parseFloat(agent.temperature),
        max_tokens: parseInt(agent.max_tokens, 10),
        system_prompt: agent.system_prompt,
      })),
    };

    await agentsSidecarAPI.updateConfig(payload);
    saveMessage.value = { type: 'success' };

    setTimeout(() => {
      saveMessage.value = null;
    }, 3000);
  } catch (err) {
    saveMessage.value = { type: 'error', text: err.message };
  } finally {
    isSaving.value = false;
  }
};

const resetConfig = async () => {
  isResetting.value = true;
  saveMessage.value = null;

  try {
    await agentsSidecarAPI.resetConfig();
    config.value = null;
    await fetchConfig();
    saveMessage.value = { type: 'success', isReset: true };

    setTimeout(() => {
      saveMessage.value = null;
    }, 3000);
  } catch (err) {
    saveMessage.value = { type: 'error', text: err.message };
  } finally {
    isResetting.value = false;
  }
};

const togglePrompt = agentName => {
  expandedPrompts.value[agentName] = !expandedPrompts.value[agentName];
};

const agentIcons = {
  gating_agent: 'i-lucide-filter',
  conversation_reader: 'i-lucide-book-open',
  responder_agent: 'i-lucide-message-circle',
  suggester_agent: 'i-lucide-lightbulb',
  escalation_agent: 'i-lucide-alert-triangle',
  global_qa_agent: 'i-lucide-search',
};

watch(
  () => props.visible,
  newVisible => {
    if (newVisible && !config.value) {
      fetchConfig();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="visible" class="absolute inset-0 z-10 bg-n-solid-1 flex flex-col">
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
    >
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ $t('AGENTS_SIDECAR.CONFIG.TITLE') }}
      </h3>
      <button
        class="p-1.5 rounded-lg hover:bg-n-solid-3 transition-colors"
        @click="emit('close')"
      >
        <span class="i-lucide-x text-n-slate-11 text-lg" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto p-4">
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <span class="i-lucide-loader-2 text-2xl text-n-brand animate-spin" />
      </div>

      <div v-else-if="error" class="text-center py-8">
        <span class="i-lucide-alert-circle text-r-500 text-2xl mb-2" />
        <p class="text-sm text-n-slate-11">{{ error }}</p>
        <button
          class="mt-3 px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-solid-3 rounded-lg hover:bg-n-solid-4 transition-colors"
          @click="fetchConfig"
        >
          {{ $t('AGENTS_SIDECAR.ACTIONS.TRY_AGAIN') }}
        </button>
      </div>

      <div v-else-if="config" class="space-y-4">
        <div class="p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
          <div class="flex items-center gap-2 mb-3">
            <span class="i-lucide-cpu text-n-brand" />
            <span class="text-xs font-semibold text-n-slate-11 uppercase">
              {{ $t('AGENTS_SIDECAR.CONFIG.LLM_PROVIDER') }}
            </span>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">
                {{ $t('AGENTS_SIDECAR.CONFIG.LLM_PROVIDER') }}
              </label>
              <select
                v-model="config.llm_provider"
                class="w-full px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/50"
              >
                <option
                  v-for="provider in LLM_PROVIDERS"
                  :key="provider"
                  :value="provider"
                >
                  {{ provider }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">
                {{ $t('AGENTS_SIDECAR.CONFIG.LLM_MODEL') }}
              </label>
              <input
                v-model="config.llm_model"
                type="text"
                class="w-full px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/50"
              />
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <h4
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ $t('AGENTS_SIDECAR.CONFIG.AGENTS') }}
          </h4>

          <div
            v-for="(agent, index) in config.agents"
            :key="agent.name"
            class="p-3 rounded-lg bg-n-solid-2 border border-n-weak"
          >
            <div class="flex items-start gap-3 mb-3">
              <div
                class="w-8 h-8 rounded-lg bg-n-brand/10 flex items-center justify-center flex-shrink-0"
              >
                <span
                  class="text-n-brand"
                  :class="[agentIcons[agent.name] || 'i-lucide-bot']"
                />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{
                    agent.name
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())
                  }}
                </p>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ agent.description }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label class="block text-xs text-n-slate-10 mb-1">
                  <span class="i-lucide-thermometer inline-block mr-1" />
                  {{ $t('AGENTS_SIDECAR.CONFIG.TEMPERATURE') }}
                </label>
                <input
                  v-model="config.agents[index].temperature"
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  class="w-full px-3 py-2 text-sm bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/50"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-10 mb-1">
                  <span class="i-lucide-hash inline-block mr-1" />
                  {{ $t('AGENTS_SIDECAR.CONFIG.MAX_TOKENS') }}
                </label>
                <input
                  v-model="config.agents[index].max_tokens"
                  type="number"
                  min="1"
                  class="w-full px-3 py-2 text-sm bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/50"
                />
              </div>
            </div>

            <div v-if="agent.system_prompt">
              <button
                class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 mb-2 transition-colors"
                @click="togglePrompt(agent.name)"
              >
                <span
                  :class="[
                    expandedPrompts[agent.name]
                      ? 'i-lucide-chevron-down'
                      : 'i-lucide-chevron-right',
                  ]"
                />
                {{ $t('AGENTS_SIDECAR.CONFIG.SYSTEM_PROMPT') }}
                <span class="text-n-slate-9">
                  ({{
                    expandedPrompts[agent.name]
                      ? $t('AGENTS_SIDECAR.CONFIG.HIDE_PROMPT')
                      : $t('AGENTS_SIDECAR.CONFIG.SHOW_PROMPT')
                  }})
                </span>
              </button>
              <textarea
                v-if="expandedPrompts[agent.name]"
                v-model="config.agents[index].system_prompt"
                rows="6"
                class="w-full px-3 py-2 text-xs bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-12 font-mono focus:outline-none focus:ring-2 focus:ring-n-brand/50 resize-y"
              />
            </div>
          </div>
        </div>

        <div
          v-if="saveMessage"
          class="p-3 rounded-lg text-sm flex items-center gap-2"
          :class="[
            saveMessage.type === 'success'
              ? 'bg-g-500/10 text-g-500'
              : 'bg-r-500/10 text-r-500',
          ]"
        >
          <span
            :class="[
              saveMessage.type === 'success'
                ? 'i-lucide-check-circle'
                : 'i-lucide-alert-circle',
            ]"
          />
          {{
            saveMessage.type === 'success'
              ? saveMessage.isReset
                ? $t('AGENTS_SIDECAR.CONFIG.RESET_SUCCESS')
                : $t('AGENTS_SIDECAR.CONFIG.SAVE_SUCCESS')
              : $t('AGENTS_SIDECAR.CONFIG.SAVE_ERROR')
          }}
          <span v-if="saveMessage.text" class="text-xs opacity-75">
            ({{ saveMessage.text }})
          </span>
        </div>

        <div class="flex gap-2">
          <button
            class="flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isResetting || isSaving"
            @click="resetConfig"
          >
            <span v-if="isResetting" class="i-lucide-loader-2 animate-spin" />
            <span v-else class="i-lucide-rotate-ccw" />
            {{ $t('AGENTS_SIDECAR.CONFIG.RESET_BUTTON') }}
          </button>
          <button
            class="flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 bg-n-brand text-white hover:bg-n-brand/90 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isSaving || isResetting"
            @click="saveConfig"
          >
            <span v-if="isSaving" class="i-lucide-loader-2 animate-spin" />
            {{
              isSaving
                ? $t('AGENTS_SIDECAR.CONFIG.SAVING')
                : $t('AGENTS_SIDECAR.CONFIG.SAVE_BUTTON')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
